import { useMemo, useState, useEffect, useCallback, useContext } from "react";
import icons from "../../../assets/images/constantIcons";
import EditTableForm from "./EditTableForm";
import apiService, { handleError } from "../../Common/ApiCommon/ApiService";
import ViewTable from "./ViewTable";
import images from "../../../assets/images/constantImages";
import DeletePopup from "./DeletePopup";
import { UserContext } from "../../Layout/UserContext";

const converttime12hrFormat = (timeRange) => {
  if (!timeRange) return "";

  const times = timeRange.split(" - ");
  if (times.length !== 2) return timeRange; // return original if format is incorrect

  const convertTo12Hr = (time) => {
    const [hours, minutes] = time.split(":").map(Number);
    const ampm = hours >= 12 ? "PM" : "AM";
    const hour = hours % 12 || 12; // Convert 0 to 12
    return `${hour}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  };

  return `${convertTo12Hr(times[0])} - ${convertTo12Hr(times[1])}`;
}
const useAddTable = () => {
  const [currentPage, setCurrentPage] = useState(1); // Track current page
  const [rowsPerPage, setRowsPerPage] = useState(15); // Track rows per page
  const [editPopupIndex, setEditPopupIndex] = useState(null); // Track which row's edit popup is open
  const [viewPopupIndex, setViewPopupIndex] = useState(null);
  const [deletePopupIndex, setDeletePopupIndex] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [filterType, setFilterType] = useState("true");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const { triggerNotificationRefresh } = useContext(UserContext);
  // Fetch table data
  const fetchTabledata = useCallback(async () => {
    setLoading(true);
    try {
      let url = "/tables";
      const params = new URLSearchParams();
      if (filterType === "true") params.append("active", "true");
      if (filterType === "false") params.append("active", "false");
      if (searchQuery) params.append("search", searchQuery);

      const response = await apiService.get(`${url}?${params.toString()}`);
      setTableData(response.data);
      triggerNotificationRefresh();
    } catch (errors) {
      handleError(errors)
    } finally {
      setLoading(false); // Set loading false after fetching
    }
  }, [filterType, searchQuery]);

  useEffect(() => {
    fetchTabledata();
  }, [filterType, searchQuery]);

  // Open edit form
  const handleEditPopup = (id) => {
    setEditPopupIndex(id);
  };

  // open for active deactive
  const handleStatus = (id) => {
    setDeletePopupIndex(id);
  };
  // Open view form
  const handleViewdetails = (id) => {
    setViewPopupIndex(id);
  };

  // Table Headings
  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
      },
      {
        Header: "TAble Name",
        accessor: "name",
      },
      {
        Header: "Day Charge",
        accessor: "day_charge",
      },
      {
        Header: "Night Charge",
        accessor: "night_charge",
      },
      {
        Header: "Day Time",
        accessor: "day_time_range",
      },
      {
        Header: "Night Time",
        accessor: "night_time_range",
      },
      {
        Header: "STATUS",
        accessor: "status",
        // disableSortBy: true ,
        Cell: ({ row }) => {
          return (
            <p
              className={`text-xs md:text-sm font-medium flex justify-center items-center rounded-3xl w-[100px] md:w-auto px-4 py-[6px] 
              ${row.original.status === "Occupied"
                  ? "text-blue bg-blue bg-opacity-[10%]"
                  : "text-red bg-red bg-opacity-[10%]"
                }`}
            >
              {row.original.status}
            </p>
          );
        },
      },
      {
        Header: "Action",
        accessor: "action",
        disableSortBy: true,
        Cell: ({ row }) => {
          const isDeactive = row.original.is_active === "Deactive";
          return (
            <div className="flex justify-center items-center gap-5">
              <div
                onClick={() => handleViewdetails(row.original.id)}
                className="flex items-center justify-center focus:outline-none w-[40px] h-[40px] rounded-full p-2 bg-green bg-opacity-[5%] cursor-pointer  text-green "
              >
                <icons.IoEyeOutline className="text-xl font-bold" />
              </div>
              {viewPopupIndex === row.original.id && (
                <ViewTable
                  isOpen={viewPopupIndex === row.original.id}
                  onClose={() => handleViewdetails(null)}
                  tableId={row.original.id}
                />
              )}
              <button
                onClick={() => !isDeactive && handleEditPopup(row.original.id)}
                className={`p-2 flex items-center justify-center focus:outline-none w-[40px] h-[40px] rounded-full ${isDeactive
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-green50 text-green cursor-pointer"
                  }`}
                disabled={isDeactive}
              >
                <icons.FaRegEdit className="text-xl font-bold" />
              </button>
              {editPopupIndex === row.original.id &&
                row.original.status !== "Deactive" && (
                  <EditTableForm
                    editPopup={editPopupIndex === row.original.id}
                    onClose={() => handleEditPopup(null)}
                    tableId={row.original.id}
                    fetchTabledata={fetchTabledata}
                  />
                )}
              <div
                className="flex items-center justify-center focus:outline-none  rounded-full p-2 cursor-pointer w-[50px]"
                onClick={() => {
                  handleStatus(row.original.id, row.original.is_active);
                }}
              >
                {row.original.is_active === "Active" ? (
                  <img src={images.Active} alt="Active" className="" />
                ) : (
                  <img src={images.Deactive} alt="Deactive" className="" />
                )}
              </div>
              {deletePopupIndex === row.original.id && (
                <DeletePopup
                  isOpen={deletePopupIndex === row.original.id}
                  onClose={() => handleStatus(null)}
                  tableName={row.original.name}
                  tableId={row.original.id}
                  fetchTabledata={fetchTabledata}
                  isActive={
                    tableData.find(
                      (tableData) => tableData.id === row.original.id
                    )?.is_active
                  }
                />
              )}
            </div>
          );
        },
      },
    ],
    [editPopupIndex, viewPopupIndex, tableData, deletePopupIndex]
  );

  const data = useMemo(() => {
    if (!Array.isArray(tableData)) return [];
    return tableData?.map((table) => ({
      id: table.id,
      category: table.category_name,
      name: table.name,
      day_charge: `₹${(table?.day_charge)}`,
      night_charge: `₹ ${(table?.night_charge)}`,
      night_time_range: converttime12hrFormat(table.night_time_range),
      day_time_range: converttime12hrFormat(table.day_time_range),
      status: table.status === "idle" ? "Idle" : "Occupied",
      is_active: table.is_active ? "Active" : "Deactive",
      action: "",
    }));
  }, [tableData]);

  // Paginate the data
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = data.slice(indexOfFirstRow, indexOfLastRow);

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  return {
    columns,
    currentRows,
    totalRows: data.length,
    currentPage,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    fetchTabledata,
    setFilterType,
    filterType,
    searchQuery,
    setSearchQuery,
    loading
  };
};

export default useAddTable;
